import { useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { useAuthStore, hasPermission, useHasModule } from '@/auth/auth-store';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { useT } from '@/i18n/use-t';

type Diagnosis = {
  checks?: Record<string, unknown>;
  publications?: {
    domain: string;
    state: string;
    publishedVersion: string;
    reportedVersion: string | null;
    reportedAt: string | null;
    confirmedReportedEffectiveAt: string | null;
  }[];
  publicationNotice?: string;
  storeDate?: string | null;
  observedAt: string;
  requestedCode: string;
  terminal: { code: string };
  store: { code: string };
  item: Record<string, unknown> | null;
  pricing: Record<string, unknown>[];
  vatGroups: unknown[];
  vatPosting: unknown[];
  vatCodes: unknown[];
  crossReferences: unknown[];
  storeVat: unknown[];
  discountBarcodeEvidence: { status: string; rows?: unknown[]; reason?: string };
  feeds: { domain: string; version: string; contentHash: string; updatedAt: string }[];
  missingFeeds: string[];
};
function Facts({ title, value }: { title: string; value: unknown }) {
  return (
    <section>
      <h2 className="font-semibold">{title}</h2>
      <pre className="overflow-x-auto whitespace-pre-wrap rounded border p-3 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    </section>
  );
}
/** Explicit support inspection; never changes a published source or lane. */
export function SanitexItemDiagnosisPage() {
  const t = useT('admin_ui');
  const licensed = useHasModule('sanitex');
  const permitted = useAuthStore((s) => hasPermission(s.user, 'sanitex.read'));
  const [terminals, setTerminals] = useState<
    { id: string; code: string; displayName: string; locationId: string }[]
  >([]);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    if (!licensed || !permitted) return;
    let cancelled = false;
    void apiClient
      .get<{ terminals: typeof terminals; truncated: boolean }>('/sanitex/diagnostics/filters')
      .then((r) => {
        if (!cancelled) {
          setTerminals(r.data.terminals);
          setTruncated(r.data.truncated);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Till list could not be loaded');
      });
    return () => {
      cancelled = true;
    };
  }, [licensed, permitted]);
  const [terminalId, setTerminalId] = useState('');
  const [code, setCode] = useState('');
  const [result, setResult] = useState<Diagnosis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const request = useRef(0);
  const clear = () => {
    request.current++;
    setResult(null);
    setError(null);
    setLoading(false);
  };
  const inspect = async () => {
    const current = ++request.current;
    setResult(null);
    setError(null);
    setLoading(true);
    try {
      const response = await apiClient.get<Diagnosis>('/sanitex/diagnostics/item', {
        params: { terminalId, code: code.trim() },
      });
      if (request.current === current) setResult(response.data);
    } catch (e) {
      if (request.current === current)
        setError(
          e instanceof Error
            ? e.message
            : t('sanitex.diagnosis.failed', 'Diagnosis could not be loaded'),
        );
    } finally {
      if (request.current === current) setLoading(false);
    }
  };
  if (!licensed || !permitted)
    return (
      <p>{t('sanitex.diagnosis.noAccess', 'Sanitex diagnosis is unavailable for this account.')}</p>
    );
  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-semibold">{t('sanitex.diagnosis.title', 'Item diagnosis')}</h1>
      <p>
        {t(
          'sanitex.diagnosis.hint',
          'Inspect the published facts for a SKU or barcode at one till. Source validity and lane versions are shown; unavailable evidence stays unavailable.',
        )}
      </p>
      <form
        className="grid gap-3 sm:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          void inspect();
        }}
      >
        <div>
          <Label htmlFor="diagnosis-till">{t('sanitex.diagnosis.till', 'Till')}</Label>
          <Select
            id="diagnosis-till"
            value={terminalId}
            onChange={(e) => {
              clear();
              setTerminalId(e.target.value);
            }}
            required
          >
            <option value="">{t('sanitex.diagnosis.chooseTill', 'Choose a till')}</option>
            {terminals.map((lane) => (
              <option key={lane.id} value={lane.id}>
                {lane.code} — {lane.displayName}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="diagnosis-code">{t('sanitex.diagnosis.code', 'SKU or barcode')}</Label>
          <Input
            id="diagnosis-code"
            value={code}
            maxLength={128}
            onChange={(e) => {
              clear();
              setCode(e.target.value);
            }}
            required
          />
        </div>
        <Button
          className="self-end"
          type="submit"
          disabled={!terminalId || !code.trim() || loading}
        >
          {loading
            ? t('sanitex.diagnosis.loading', 'Loading…')
            : t('sanitex.diagnosis.inspect', 'Inspect')}
        </Button>
      </form>
      {truncated && (
        <p role="status">
          {t(
            'sanitex.diagnosis.truncated',
            'Only the first 1000 bound tills are listed. Narrow the source estate before inspection.',
          )}
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      {result && (
        <div className="space-y-4">
          <p>
            {result.store.code} / {result.terminal.code} — {result.requestedCode} —{' '}
            {result.observedAt}
          </p>
          {!result.item && (
            <p role="status">
              {t(
                'sanitex.diagnosis.notFound',
                'No published item matches this SKU or barcode at this store.',
              )}
            </p>
          )}
          {result.missingFeeds.length > 0 && (
            <p role="status">
              {t('sanitex.diagnosis.missingFeeds', 'Unavailable feeds')}:{' '}
              {result.missingFeeds.join(', ')}
            </p>
          )}
          <section className="space-y-2">
            <h2 className="font-semibold">
              {t('sanitex.readiness.title', 'Item readiness and price publication at this till')}
            </h2>
            <p>
              {t(
                'sanitex.readiness.notice',
                'A matching report confirms when the lane reported loading this generation. Pending or missing reports have no confirmed effective time. Live execution is not attested.',
              )}
            </p>
            <p>
              {t('sanitex.readiness.storeDate', 'Store date')}:{' '}
              {result.storeDate ?? t('sanitex.readiness.unknown', 'Unavailable')}
            </p>
            <Facts
              title={t('sanitex.readiness.checks', 'Item data checks (null means unavailable)')}
              value={result.checks}
            />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th>{t('sanitex.readiness.feed', 'Feed')}</th>
                    <th>{t('sanitex.readiness.publication', 'Published version')}</th>
                    <th>{t('sanitex.readiness.lane', 'Reported lane version')}</th>
                    <th>{t('sanitex.readiness.state', 'Publication state')}</th>
                    <th>{t('sanitex.readiness.reportTime', 'Report received')}</th>
                    <th>
                      {t('sanitex.readiness.effective', 'Confirmed reported application time')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {result.publications?.map((p) => (
                    <tr key={p.domain}>
                      <td>{p.domain}</td>
                      <td>{p.publishedVersion}</td>
                      <td>{p.reportedVersion ?? '—'}</td>
                      <td>{p.state}</td>
                      <td>{p.reportedAt ?? '—'}</td>
                      <td>
                        {p.confirmedReportedEffectiveAt ??
                          t('sanitex.readiness.unconfirmed', 'Not confirmed')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <Facts
            title={t(
              'sanitex.diagnosis.itemFacts',
              'Item, barcodes, blocked status, units and distribution',
            )}
            value={result.item}
          />
          <Facts
            title={t(
              'sanitex.diagnosis.priceFacts',
              'Price rows, line discounts, offers and validity windows',
            )}
            value={result.pricing}
          />
          <Facts
            title={t('sanitex.diagnosis.markdown', 'Discount barcodes and original validity')}
            value={result.discountBarcodeEvidence}
          />
          <Facts
            title={t('sanitex.diagnosis.tax', 'Published VAT and tax records')}
            value={{
              groups: result.vatGroups,
              posting: result.vatPosting,
              codes: result.vatCodes,
              store: result.storeVat,
            }}
          />
          <Facts
            title={t('sanitex.diagnosis.crossReferences', 'Package and item cross references')}
            value={result.crossReferences}
          />
          <Facts
            title={t('sanitex.diagnosis.versions', 'Published feed versions and update times')}
            value={result.feeds}
          />
        </div>
      )}
    </div>
  );
}
